import { useState } from "react";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Vault, Landmark, ArrowDownToLine } from "lucide-react";
import { usePoll } from "@/lib/usePoll";
import { api, errMsg, usd, num, dt } from "@/lib/api";
import { Panel, Metric, Empty, PageTitle, Badge } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function Reconcile() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { setR((await api.post("/account/sync")).data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const verify = async () => {
    setBusy(true);
    try { const v = (await api.post("/kraken/verify")).data; setR({ checks: Object.entries(v).map(([k, x]) => ({ check: k, ok: typeof x === "object" ? x?.ok !== false : x !== "not configured", detail: typeof x === "object" ? JSON.stringify(x).slice(0, 140) : x })) }); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Panel title="Sync & reconcile with Kraken" icon={RefreshCw} testid="reconcile-panel">
      <div className="flex flex-wrap gap-2 mb-3">
        <Button data-testid="sync-button" size="sm" onClick={go} disabled={busy} className="bg-[#F7931A] hover:bg-[#E08213] text-black">Synchronize & reconcile</Button>
        <Button data-testid="verify-kraken-button" size="sm" variant="secondary" onClick={verify} disabled={busy}>Verify API (validate-only order)</Button>
      </div>
      {r?.checks?.map((c, i) => (<div key={i} className={`font-mono text-[11px] py-1 border-b border-zinc-800/50 ${c.ok ? "text-emerald-400" : "text-red-400"}`}>{c.ok ? "✓" : "✗"} {c.check} <span className="text-zinc-500">{c.detail}</span></div>))}
    </Panel>
  );
}

function DepositPanel() {
  const [addr, setAddr] = useState(null);
  const get = async () => {
    try { setAddr((await api.get("/account/deposit-address?asset=XBT&method=Bitcoin")).data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Deposit to Kraken" icon={ArrowDownToLine} testid="deposit-panel">
      <ol className="text-sm text-zinc-400 space-y-1.5 list-decimal pl-4">
        <li>Deposit USD/EUR via bank transfer in Kraken → Funding → Deposit, or send BTC to your Kraken deposit address.</li>
        <li>After it is credited, press Synchronize, then allocate the deposit to active capital below (deposits never count as trading profit).</li>
      </ol>
      <div className="flex flex-wrap gap-2 mt-4">
        <a href="https://www.kraken.com/u/funding/deposit" target="_blank" rel="noreferrer" data-testid="kraken-deposit-link"><Button size="sm" variant="secondary">Open Kraken deposit <ExternalLink className="w-3 h-3 ml-1" /></Button></a>
        <Button size="sm" variant="secondary" onClick={get} data-testid="get-btc-address-button">Show BTC deposit address</Button>
      </div>
      {addr && <pre data-testid="deposit-address" className="mt-3 font-mono text-[11px] text-zinc-300 bg-[#090A0F] p-3 rounded overflow-auto max-h-32">{JSON.stringify(addr, null, 2)}</pre>}
    </Panel>
  );
}

function LedgerPanel({ reload }) {
  const { data, error } = usePoll("/account/ledger", 0);
  const [pw, setPw] = useState("");
  const alloc = async (refid) => {
    try { await api.post("/account/allocate-deposit", { refid, password: pw }); toast.success("Deposit allocated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Kraken deposits & withdrawals" icon={Landmark} testid="ledger-panel" right={<Input type="password" placeholder="Password to allocate" value={pw} onChange={(e) => setPw(e.target.value)} className="h-8 w-44 text-xs" data-testid="allocate-password-input" />}>
      {error ? <Empty>{errMsg(error)}</Empty> : !data?.length ? <Empty>No deposits or withdrawals found on Kraken.</Empty> : (
        <div className="overflow-x-auto"><table className="w-full font-mono text-xs"><thead><tr className="text-left text-[10px] text-zinc-500 border-b border-zinc-800"><th className="py-2">Time</th><th>Type</th><th>Asset</th><th className="text-right">Amount</th><th className="text-right">Fee</th><th></th></tr></thead>
          <tbody>{data.map((l) => (<tr key={l.ledger_id} className="border-b border-zinc-800/50"><td className="py-2 text-zinc-400">{new Date(l.time * 1000).toLocaleString()}</td><td>{l.type}</td><td>{l.asset}</td><td className="text-right">{l.amount}</td><td className="text-right">{l.fee}</td>
            <td className="text-right">{l.type === "deposit" && ["ZUSD", "USD"].includes(l.asset) ? (l.allocated ? <Badge v="ok">allocated</Badge> : <Button size="sm" variant="secondary" disabled={!pw} onClick={() => alloc(l.refid)} data-testid={`allocate-${l.refid}`}>Allocate</Button>) : null}</td></tr>))}</tbody></table></div>)}
    </Panel>
  );
}

function ReservePanel({ a, reload }) {
  const [amt, setAmt] = useState("");
  const [pw, setPw] = useState("");
  const [ref, setRef] = useState("");
  const post = async (path) => {
    try { await api.post(path, { amount: parseFloat(amt), password: pw, refid: ref || null }); toast.success("Recorded"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const l = a?.ledger || {};
  return (
    <Panel title="Permanent profit reserve" icon={Vault} testid="reserve-panel">
      <div className="font-mono text-2xl text-blue-400 font-bold">{usd(l.reserve, 4)}</div>
      <div className="mt-1"><Badge v={a?.reserve_status?.startsWith("ledger") ? "data_unavailable" : "ok"} testid="reserve-status">{a?.reserve_status}</Badge></div>
      <p className="text-xs text-zinc-500 mt-3">The bot's API key has no withdrawal permission. To protect the reserve physically, move it yourself to a separate non-trading Kraken account or wallet, then confirm the amount here. Reserve withdrawals are executed by you on Kraken and recorded here — the bot never withdraws.</p>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <Input placeholder="Amount (USD)" type="number" value={amt} onChange={(e) => setAmt(e.target.value)} className="h-8 text-xs" data-testid="reserve-amount-input" />
        <Input placeholder="Kraken refid (optional)" value={ref} onChange={(e) => setRef(e.target.value)} className="h-8 text-xs" data-testid="reserve-refid-input" />
        <Input placeholder="Password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="h-8 text-xs col-span-2" data-testid="reserve-password-input" />
        <Button size="sm" variant="secondary" disabled={!pw || !amt} onClick={() => post("/account/reserve-segregation")} data-testid="reserve-segregate-button">Confirm segregated</Button>
        <Button size="sm" variant="secondary" disabled={!pw || !amt} onClick={() => post("/account/reserve-withdrawal")} data-testid="reserve-withdraw-button">Record withdrawal</Button>
      </div>
      <a href="https://www.kraken.com/u/funding/withdraw" target="_blank" rel="noreferrer" data-testid="kraken-withdraw-link" className="inline-flex items-center gap-1 text-xs text-[#F7931A] mt-4 hover:underline">Open Kraken for manual withdrawal <ExternalLink className="w-3 h-3" /></a>
    </Panel>
  );
}

export default function Account() {
  const { data: a, reload } = usePoll("/account", 20000);
  const l = a?.ledger || {};
  const sp = a?.spot || {};
  return (
    <div>
      <PageTitle title="Account & Kraken" sub="Bot ledger vs verified Kraken balances. Trading capital the engine may use = min(ledger active capital, Kraken available balance − unsegregated reserve)." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Metric testid="acct-active" label="Active trading capital (ledger)" value={usd(l.active_capital, 4)} />
        <Metric testid="acct-tradable-futures" label="Tradable now · futures" value={a?.tradable_futures ? usd(a.tradable_futures.capital, 4) : "—"} sub={a?.errors?.futures || (a?.tradable_futures?.error ?? "Kraken-verified")} />
        <Metric testid="acct-tradable-spot" label="Tradable now · spot" value={a?.tradable_spot ? usd(a.tradable_spot.capital, 4) : "—"} sub={a?.errors?.spot || (a?.tradable_spot?.error ?? "Kraken-verified")} />
        <Metric testid="acct-reserve" label="Protected reserve" value={usd(l.reserve, 4)} tone="blue" sub={`segregated ${usd(l.reserve_segregated, 2)}`} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <Panel title="Kraken balances (verified)" testid="kraken-balances">
          {a?.errors?.spot && !a?.spot ? <Empty>{a.errors.spot}</Empty> : (
            <div className="space-y-1">{Object.entries(sp).filter(([, v]) => parseFloat(v.balance) > 0).map(([k, v]) => (
              <div key={k} className="flex justify-between font-mono text-xs border-b border-zinc-800/60 py-1.5"><span className="text-zinc-300">{k}</span><span className="text-zinc-400">{v.balance} <span className="text-zinc-600">(hold {v.hold_trade})</span></span></div>))}</div>)}
          <div className="mt-4 font-mono text-[10px] text-zinc-500">FUTURES (FLEX)</div>
          {a?.futures ? <div className="font-mono text-xs text-zinc-300 mt-1">Portfolio {usd(a.futures.portfolio_value)} · available margin {usd(a.futures.available_margin)} · PnL {num(a.futures.pnl, 4)}</div> : <div className="text-xs text-zinc-500 mt-1">{a?.errors?.futures}</div>}
        </Panel>
        <ReservePanel a={a} reload={reload} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mb-4"><Reconcile /><DepositPanel /></div>
      <LedgerPanel reload={reload} />
      {(a?.capital_events || []).length > 0 && <Panel title="Capital events" className="mt-4" testid="capital-events">{a.capital_events.map((e) => (<div key={e.refid} className="font-mono text-xs text-zinc-400 py-1">{dt(e.ts)} · {e.type} · {usd(e.amount)} · {e.refid}</div>))}</Panel>}
    </div>
  );
}
